"use client";
import { useState, useCallback, useEffect } from "react";
import { LinearProgress, Stack } from "@mui/material";

import { observer, useStore } from "../../../../../service/mobx";
import { Container } from "..";
import Skeleton from "../../../../../component/Skeleton";
import defaultInputs from "./defaultInputs.json";
import RunButton from "./RunButtton";

function InputBox({ label, Input, Examples, small = false }) {
  const [invalidInput, setInvalid] = useState(false);
  const [notRunYet, setNotRunYet] = useState(true);
  const [objectUrl, setObjectUrl] = useState();
  const { analytics, device, model, snackbar, user } = useStore();
  const noInput = model.input === undefined;
  const disabled = model.running || invalidInput;

  useEffect(() => {
    if (noInput && model.node.task !== "chat") {
      model.set.input(defaultInputs[model.node.task]);
    }
  }, [model, noInput]);

  useEffect(() => {
    if (0 < model.clusterDoc.inferences) {
      // if the last date we ran inference is less than timeout seconds
      // so like we ran inference less than 90 seconds ago, sort of thing
      const runInferenceRecently =
        (model.clusterDoc.lastInference.toDate().getTime() - Date.now()) /
          1000 <
        model.clusterDoc.timeout;

      setNotRunYet(runInferenceRecently === false);
    }
  }, [model.clusterDoc]);

  useEffect(() => {
    if (objectUrl) {
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [objectUrl]);

  const runModel = useCallback(async () => {
    try {
      var {
        input,
        node: { modelId, task, isLanguageModel }
      } = model;

      model.set.running(true);

      if (notRunYet) {
        setNotRunYet(false);

        snackbar.notify({
          duration: 10e3,
          line1: "Provisioning hardware...",
          line2: "This may take 1 minute, so go grab a coffee and come back :)"
        });
      }
      const response = await fetch(`/api/model/widget/${modelId}`, {
        method: "POST",
        signal: AbortSignal.timeout(16 * 60e3),
        body: JSON.stringify({ key: user.key, task, input })
      });
      const contentType = response.headers.get("content-type") ?? "";

      if (
        isLanguageModel &&
        contentType.includes("application/json") === false
      ) {
        try {
          const readableStream = response.body
            .pipeThrough(new TextDecoderStream())
            .getReader();
          let text = "";

          do {
            var { done, value } = await readableStream.read();

            if (model.node.modelId === modelId) {
              if (done === false) {
                text += value;

                if (task === "chat") {
                  output ??= { role: "assistant", content: "" };
                  output.content += value;

                  model.set.output([...input, output]);
                } else {
                  model.set.output(text);
                }

                model.set.error();
              }
            }
            // console.log({ value });
          } while (done === false);

          // console.log("streamed", { text });
        } catch (error) {
          console.error(error);
          model.set.error(error);
        }
      } else {
        var { error, output } = await response.json();

        // console.log("output received", { error, output });
        //
        if (error) {
          model.set.output();
          model.set.error(error);
          snackbar.notify({ line1: "Error", line2: error });
        } else {
          model.set.error();

          if (["text-to-speech", "text-to-audio"].includes(task)) {
            if (output.startsWith("http")) {
              var objectUrl = output;
            } else {
              const [data, base64] = output.split(";base64,");
              const decoded = atob(base64 || data);
              const byteLength = decoded.length;
              const byteArray = new Uint8Array(byteLength);

              for (let i = 0; i < byteLength; i++) {
                byteArray[i] = decoded.charCodeAt(i);
              }

              objectUrl = URL.createObjectURL(
                new Blob([byteArray], {
                  type: data.split(":")[1] ?? "audio/wav"
                })
              );
            }

            setObjectUrl(objectUrl);
            model.set.output(objectUrl);
          } else {
            model.set.output(output);
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      model.set.running();
      analytics.track.event("Model Run", { modelId });
    }
  }, [analytics, model, snackbar, user, notRunYet]);

  return (
    <Container label={label} small={small} key={user.isAnonymous}>
      {model.running ? <LinearProgress /> : null}
      <Input
        setInvalid={setInvalid}
        invalidInput={invalidInput}
        runModel={runModel}
      />
      {Examples ? <Examples setInvalid={setInvalid} /> : null}
      {model.node.task === "chat" ? null : (
        <Stack
          useFlexGap
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
        >
          {user.loaded ? (
            <RunButton
              label="Run model"
              fullWidth={device.isPhone}
              isOnApiPlan={!model.notOnApiPlan}
              allowed={model.allowed}
              disabled={disabled}
              modelSize={model.node.sizeName}
              runModel={runModel}
              sx={{
                borderRadius: "var(--shape-sm)",
                height: { compact: 48, expanded: 40 },
                typography: { compact: "titleMd", expanded: "labelMd" }
              }}
            />
          ) : (
            <Skeleton width={80} height={40} borderRadius="round" />
          )}
        </Stack>
      )}
    </Container>
  );
}

export default observer(InputBox);
